
import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Brain,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  CircleX,
  Clock,
  Dumbbell,
  GraduationCap,
  HeartPulse,
  Mail,
  Phone,
  Search,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Users,
  X,
} from "lucide-react";

/* =========================================================
   DOCTOR DATA
========================================================= */

const doctors = [
  {
    id: 1,
    name: "Md. Wasif Wali Farabe",
    qualification: "BPT (GB)",
    education: [
      "MS – Disability Management & Rehabilitation (BOU)",
      "PGT-MM (UK)",
    ],
    role: "Consultant & Chief Physiotherapy Officer",
    specialties: [
      "Dry Needling & Acupuncture",
      "Pain & Posture Management",
      "Sports Injury",
      "Post-operative Orthopedic Rehabilitation",
      "Neuro Rehabilitation",
    ],
    shortDescription:
      "Specialist in pain management, posture correction, sports injury rehabilitation, and orthopedic and neurological rehabilitation.",
    category: "Orthopedic",
    accent: "from-blue-600 to-indigo-600",
    softBg: "bg-blue-50",
    icon: BoneIcon,
    initials: "MW",
    image: "",
  },
  {
    id: 2,
    name: "Sayem Sadik",
    qualification: "BPT (ADTU), India",
    education: ["Bachelor of Physiotherapy (ADTU), India"],
    role: "Clinical In-charge & Senior Physiotherapy Officer",
    specialties: [
      "Orthopedic & Musculoskeletal Physiotherapy",
      "Neuropathic Pain Management",
      "Sports Injury",
      "Post-operative Rehabilitation",
    ],
    shortDescription:
      "Focuses on musculoskeletal rehabilitation, neuropathic pain management, sports injuries, and post-operative recovery.",
    category: "Orthopedic",
    accent: "from-cyan-500 to-blue-600",
    softBg: "bg-cyan-50",
    icon: BoneIcon,
    initials: "SS",
    image: "",
  },
  {
    id: 3,
    name: "Akash Singh",
    qualification: "BPT (GB)",
    education: ["Bachelor of Physiotherapy"],
    role: "Physiotherapy Officer",
    specialties: [
      "Stroke Rehabilitation",
      "Pediatric Physiotherapy",
      "Pain Management",
    ],
    shortDescription:
      "Provides physiotherapy support for stroke rehabilitation, pediatric care, and pain management.",
    category: "Neurological",
    accent: "from-violet-500 to-blue-600",
    softBg: "bg-violet-50",
    icon: Brain,
    initials: "AS",
    image: "",
  },
  {
    id: 4,
    name: "Robin Dey",
    qualification: "BPT (ADTU), India",
    education: ["Bachelor of Physiotherapy (ADTU), India"],
    role: "Senior Physiotherapy Officer",
    specialties: [
      "Geriatric Rehabilitation",
      "Spinal Cord Injury",
      "Pain Management",
      "Post-operative Rehabilitation",
      "Neuro Rehabilitation",
    ],
    shortDescription:
      "Specialized in geriatric rehabilitation, spinal cord injury support, pain management, and neurological rehabilitation.",
    category: "Geriatric",
    accent: "from-emerald-500 to-teal-600",
    softBg: "bg-emerald-50",
    icon: HeartPulse,
    initials: "RD",
    image: "",
  },
  {
    id: 5,
    name: "Tanvin Sweety",
    qualification: "BPT (LPU), Punjab, India",
    education: ["Bachelor of Physiotherapy (LPU), Punjab, India"],
    role: "Physiotherapy Officer",
    specialties: [
      "Gynecological Physiotherapy",
      "Women's Health & Wellness",
      "Pediatric Care",
      "Pain Management",
      "Neuro Rehabilitation",
    ],
    shortDescription:
      "Focuses on gynecological physiotherapy, women's health and wellness, pediatric care, and rehabilitation.",
    category: "Women's Health",
    accent: "from-rose-500 to-pink-600",
    softBg: "bg-rose-50",
    icon: HeartPulse,
    initials: "TS",
    image: "",
  },
  {
    id: 6,
    name: "Imran Sarwar Emon",
    qualification: "BPT (LPU, India)",
    education: [
      "Diploma in Football Medicine (F-MARC, Switzerland)",
    ],
    role: "Physiotherapy Officer",
    specialties: [
      "Spine & Sports Injury",
      "Pain Management",
      "Paralysis Rehabilitation",
      "Sports Rehabilitation",
    ],
    shortDescription:
      "Focuses on spine and sports injuries, pain management, paralysis rehabilitation, and sports rehabilitation.",
    category: "Sports",
    accent: "from-orange-500 to-amber-600",
    softBg: "bg-orange-50",
    icon: Dumbbell,
    initials: "IE",
    image: "",
  },
];

/* =========================================================
   ICON FALLBACKS
========================================================= */

function BoneIcon({ size = 24 }) {
  return <Dumbbell size={size} />;
}

/* =========================================================
   ANIMATION VARIANTS
========================================================= */

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

/* =========================================================
   DOCTOR AVATAR
========================================================= */

function DoctorAvatar({ doctor, large = false }) {
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-br ${doctor.accent} ${
        large ? "h-36 w-36" : "h-24 w-24"
      }`}
    >
      {doctor.image ? (
        <img
          src={doctor.image}
          alt={doctor.name}
          className="h-full w-full object-cover"
        />
      ) : (
        <span
          className={`font-black tracking-tight text-white ${
            large ? "text-4xl" : "text-2xl"
          }`}
        >
          {doctor.initials}
        </span>
      )}

      <div className="pointer-events-none absolute inset-0 rounded-full border-4 border-white/30" />
    </div>
  );
}

/* =========================================================
   SECTION HEADING
========================================================= */

function SectionHeading() {
  return (
    <motion.div
      variants={itemVariants}
      className="mx-auto max-w-3xl text-center"
    >
      <span className="inline-flex items-center gap-2 rounded-full bg-blue-100 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.18em] text-blue-700">
        <Stethoscope size={14} />
        Our Medical Team
      </span>

      <h2 className="mt-5 text-3xl font-black leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
        Meet Our Expert
        <span className="block text-blue-600">Physiotherapy Team</span>
      </h2>

      <p className="mt-5 text-base leading-8 text-slate-600">
        Our dedicated team of physiotherapy professionals provides
        rehabilitation support across a range of specialties. Explore the
        expertise of our clinical team at Pro Cesta Physiotherapy Hospital.
      </p>
    </motion.div>
  );
}

/* =========================================================
   DOCTOR CARD
========================================================= */

function DoctorCard({ doctor, onView }) {
  const Icon = doctor.icon;

  return (
    <motion.article
      variants={itemVariants}
      whileHover={{ y: -8 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-2xl hover:shadow-blue-100/70"
    >
      {/* Card Top Accent */}
      <div className={`h-2 bg-gradient-to-r ${doctor.accent}`} />

      {/* Background Glow */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-blue-50 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="relative">
            <DoctorAvatar doctor={doctor} />

            <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full border-4 border-white bg-emerald-500 text-white shadow-sm">
              <BadgeCheck size={15} />
            </span>
          </div>

          <span className="rounded-full bg-slate-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
            Physiotherapy
          </span>
        </div>

        {/* Name */}
        <div className="mt-6">
          <h3 className="text-xl font-black leading-snug text-slate-900">
            {doctor.name}
          </h3>

          <p className="mt-2 text-sm font-bold text-blue-600">
            {doctor.qualification}
          </p>

          <p className="mt-2 flex items-center gap-2 text-sm font-semibold text-slate-500">
            <Stethoscope size={15} />
            {doctor.role}
          </p>
        </div>

        {/* Description */}
        <p className="mt-5 text-sm leading-7 text-slate-500">
          {doctor.shortDescription}
        </p>

        {/* Specialty Pills */}
        <div className="mt-5 flex flex-wrap gap-2">
          {doctor.specialties.slice(0, 3).map((specialty) => (
            <span
              key={specialty}
              className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px] font-semibold leading-4 text-slate-600"
            >
              {specialty}
            </span>
          ))}

          {doctor.specialties.length > 3 && (
            <span className="rounded-lg bg-blue-50 px-2.5 py-1.5 text-[11px] font-bold text-blue-600">
              +{doctor.specialties.length - 3} More
            </span>
          )}
        </div>

        {/* Action */}
        <button
          onClick={() => onView(doctor)}
          className="mt-6 flex w-full items-center justify-between rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3.5 text-sm font-extrabold text-blue-700 transition hover:bg-blue-600 hover:text-white"
        >
          View Full Profile
          <ArrowRight
            size={18}
            className="transition-transform group-hover:translate-x-1"
          />
        </button>
      </div>
    </motion.article>
  );
}

/* =========================================================
   DOCTOR DETAILS MODAL
========================================================= */

function DoctorModal({ doctor, onClose }) {
  if (!doctor) return null;

  const Icon = doctor.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.95 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[2rem] bg-white shadow-2xl"
      >
        {/* Modal Header */}
        <div
          className={`relative overflow-hidden bg-gradient-to-br ${doctor.accent} px-6 pb-10 pt-8 text-white sm:px-10`}
        >
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border-[30px] border-white/10" />

          <button
            onClick={onClose}
            className="absolute right-5 top-5 rounded-full bg-white/15 p-2 text-white transition hover:bg-white/30"
            aria-label="Close profile"
          >
            <X size={20} />
          </button>

          <div className="relative flex flex-col items-center text-center sm:flex-row sm:items-center sm:text-left">
            <div className="shrink-0 rounded-full border-4 border-white/30 p-1">
              <DoctorAvatar doctor={doctor} large />
            </div>

            <div className="mt-5 sm:ml-6 sm:mt-0">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/75">
                Pro Cesta Physiotherapy Hospital
              </p>

              <h3 className="mt-2 text-2xl font-black sm:text-3xl">
                {doctor.name}
              </h3>

              <p className="mt-2 font-semibold text-white/90">
                {doctor.qualification}
              </p>

              <p className="mt-2 text-sm text-white/80">
                {doctor.role}
              </p>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 sm:p-10">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
              <Icon size={23} />
            </div>

            <h4 className="text-xl font-black text-slate-900">
              Professional Expertise
            </h4>
          </div>

          <p className="mt-4 leading-8 text-slate-600">
            {doctor.shortDescription}
          </p>

          {/* Education */}
          <div className="mt-8">
            <h4 className="flex items-center gap-2 font-extrabold text-slate-900">
              <GraduationCap size={20} className="text-blue-600" />
              Qualifications & Education
            </h4>

            <ul className="mt-4 space-y-3">
              {doctor.education.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-sm leading-6 text-slate-600"
                >
                  <CheckCircle2
                    size={17}
                    className="mt-1 shrink-0 text-emerald-500"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Expertise */}
          <div className="mt-8">
            <h4 className="flex items-center gap-2 font-extrabold text-slate-900">
              <Award size={20} className="text-blue-600" />
              Areas of Expertise
            </h4>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {doctor.specialties.map((item) => (
                <div
                  key={item}
                  className="flex items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm leading-6 text-slate-600"
                >
                  <CheckCircle2
                    size={17}
                    className="mt-1 shrink-0 text-blue-500"
                  />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Modal Footer */}
          <div className="mt-8 rounded-2xl border border-blue-100 bg-blue-50 p-5">
            <div className="flex items-start gap-3">
              <ShieldCheck
                size={22}
                className="mt-0.5 shrink-0 text-blue-600"
              />

              <div>
                <h5 className="font-bold text-slate-900">
                  Patient Care & Consultation
                </h5>

                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Contact Pro Cesta Physiotherapy Hospital to inquire about
                  appointments and available services.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#contact"
              onClick={onClose}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-blue-600 px-5 py-4 text-sm font-bold text-white transition hover:bg-blue-700"
            >
              <CalendarCheck size={18} />
              Appointment Inquiry
            </a>

            <button
              onClick={onClose}
              className="rounded-full border border-slate-200 px-6 py-4 text-sm font-bold text-slate-600 transition hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* =========================================================
   MAIN DOCTORS SECTION
========================================================= */

export default function Doctors() {
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = [
    "All",
    "Orthopedic",
    "Neurological",
    "Geriatric",
    "Women's Health",
    "Sports",
  ];

  const filteredDoctors = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return doctors.filter((doctor) => {
      const matchesCategory =
        activeCategory === "All" ||
        doctor.category === activeCategory;

      const searchableText = [
        doctor.name,
        doctor.qualification,
        doctor.role,
        doctor.category,
        doctor.shortDescription,
        ...doctor.specialties,
      ]
        .join(" ")
        .toLowerCase();

      const matchesSearch =
        !query || searchableText.includes(query);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchTerm]);

  return (
    <>
      <section
        id="doctors"
        className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-blue-50/40 px-5 py-20 lg:px-8 lg:py-28"
      >
        {/* Background Decorative Elements */}
        <div className="pointer-events-none absolute -left-40 top-20 h-96 w-96 rounded-full bg-blue-100/40 blur-3xl" />
        <div className="pointer-events-none absolute -right-40 top-96 h-96 w-96 rounded-full bg-cyan-100/40 blur-3xl" />

        <div className="relative mx-auto max-w-7xl">
          {/* Header */}
          <SectionHeading />

          {/* Statistics */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mx-auto mt-12 grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-4"
          >
            {[
              {
                icon: Users,
                value: "06",
                label: "Team Members",
              },
              {
                icon: Stethoscope,
                value: "08+",
                label: "Specialties",
              },
              {
                icon: HeartPulse,
                value: "Care",
                label: "Patient Focused",
              },
              {
                icon: ShieldCheck,
                value: "Pro",
                label: "Rehabilitation",
              },
            ].map((stat) => {
              const Icon = stat.icon;

              return (
                <motion.div
                  key={stat.label}
                  variants={itemVariants}
                  className="rounded-2xl border border-slate-100 bg-white p-4 text-center shadow-sm sm:p-5"
                >
                  <Icon className="mx-auto text-blue-600" size={22} />

                  <p className="mt-2 text-xl font-black text-slate-900">
                    {stat.value}
                  </p>

                  <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 sm:text-xs">
                    {stat.label}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Search + Filters */}
          <div className="mt-16 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-sm">
              <Search
                size={19}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search doctors or specialties..."
                className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-11 pr-5 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              />
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap rounded-full px-4 py-2.5 text-xs font-bold transition sm:text-sm ${
                    activeCategory === category
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Doctor Cards */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.05 }}
            className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredDoctors.map((doctor) => (
              <DoctorCard
                key={doctor.id}
                doctor={doctor}
                onView={setSelectedDoctor}
              />
            ))}
          </motion.div>

          {/* Empty Search State */}
          {filteredDoctors.length === 0 && (
            <div className="py-20 text-center">
              <Search
                size={40}
                className="mx-auto text-slate-300"
              />

              <h3 className="mt-4 text-xl font-bold text-slate-800">
                No doctors found
              </h3>

              <p className="mt-2 text-sm text-slate-500">
                Try a different name or specialty.
              </p>

              <button
                onClick={() => {
                  setSearchTerm("");
                  setActiveCategory("All");
                }}
                className="mt-5 rounded-full bg-blue-600 px-6 py-3 text-sm font-bold text-white"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* Bottom Banner */}
          <motion.div
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative mt-20 overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-700 to-cyan-500 px-6 py-12 text-center text-white shadow-xl shadow-blue-100 sm:px-10"
          >
            <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full border-[40px] border-white/10" />
            <div className="absolute -bottom-28 -left-24 h-72 w-72 rounded-full border-[40px] border-white/10" />

            <div className="relative">
              <Sparkles className="mx-auto text-cyan-200" size={30} />

              <h3 className="mt-4 text-2xl font-black sm:text-3xl">
                Your Recovery. Our Commitment.
              </h3>

              <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-blue-50 sm:text-base">
                Learn more about our rehabilitation services and contact our
                team to discuss your physiotherapy needs.
              </p>

              <a
                href="#contact"
                className="mt-7 inline-flex items-center gap-3 rounded-full bg-white px-7 py-4 text-sm font-extrabold text-blue-700 shadow-lg transition hover:bg-blue-50"
              >
                Contact Our Team
                <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Doctor Details Modal */}
      <AnimatePresence>
        {selectedDoctor && (
          <DoctorModal
            doctor={selectedDoctor}
            onClose={() => setSelectedDoctor(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
}